/**
 * Toolbox catalog API helpers.
 *
 * GET    /api/v1/toolbox/catalog
 * PUT    /api/v1/toolbox/catalog
 * POST   /api/v1/toolbox/catalog/reset
 * POST   /api/v1/toolbox/product-types
 * POST   /api/v1/toolbox/tools
 * PATCH  /api/v1/toolbox/tools/{tool_id}
 */
import axios from 'axios';
import {
  TOOLBOX_BOM_UNITS,
  type ElementType,
  type ToolboxBomLine,
  type ToolboxBomUnit,
  type ToolboxCustomField,
  type ToolboxItem,
} from '../types';
import {
  getToolboxCatalogResetUrl,
  getToolboxCatalogUrl,
  getToolboxProductTypesUrl,
  getToolboxToolUrl,
  getToolboxToolsUrl,
} from './apiConfig';
import { http } from './http';

/** Catalog API still requires price; UI no longer collects it. */
export const TOOLBOX_BOM_CATALOG_PRICE = 1;

const BOM_UNIT_VALUES = new Set<string>(TOOLBOX_BOM_UNITS.map((unit) => unit.value));

const DEFAULT_TIMEOUT_MS = 30000;

export type ToolboxProductType = {
  id: string;
  label: string;
  color: string;
  elementTypes: ElementType[];
  sortOrder?: number;
  isSystem?: boolean;
  toolCount?: number;
};

export type ToolboxStandardParam = {
  key: string;
  label: string;
  unit: string | null;
  defaultValue: number | null;
};

export type ToolboxCatalog = {
  productTypes: ToolboxProductType[];
  tools: ToolboxItem[];
  standardParams: ToolboxStandardParam[];
};

type ApiToolDimensions = {
  width?: number | null;
  height?: number | null;
  depth?: number | null;
};

type ApiBomEntry = {
  id?: string | null;
  name: string;
  price?: number;
  qty?: number;
  category?: string | null;
  description?: string | null;
  unit?: string | null;
};

type ApiCustomField = {
  id?: string | null;
  label: string;
  value?: unknown;
  unit?: string | null;
};

type ApiTool = {
  id: string;
  productTypeId: string;
  equipmentTypeKey?: string | null;
  equipmentTypeLabel?: string | null;
  type: string;
  name: string;
  icon: string;
  sku?: string | null;
  description?: string | null;
  defaultDimensions?: ApiToolDimensions | null;
  defaultRotation?: number;
  defaultProperties?: Record<string, unknown> | null;
  enabledParams?: string[];
  customFields?: ApiCustomField[];
  bom?: ApiBomEntry[];
  weightKg?: number | null;
  visibleInToolbox?: boolean;
};

type ApiProductType = {
  id: string;
  label: string;
  color?: string | null;
  elementTypes?: string[];
  sortOrder?: number;
  isSystem?: boolean;
  toolCount?: number;
};

type ApiCatalogResponse = {
  productTypes?: ApiProductType[];
  tools?: ApiTool[];
  standardParams?: Array<{
    key: string;
    label: string;
    unit?: string | null;
    defaultValue?: number | null;
  }>;
};

const KNOWN_ELEMENT_TYPES = new Set<string>([
  'overhead-rail',
  'overhead-curve',
  'cross-rail',
  'oht-vehicle',
  'stocker',
  'stb',
  'ohb',
  'lifter',
  'rectangle',
  'circle',
  'text',
  'support-pillar',
  'horizontal-beam',
  'suspension-rod',
]);

function readTimeoutMs(): number {
  const raw = import.meta.env.VITE_LOGIN_TIMEOUT_MS;
  if (typeof raw !== 'string') return DEFAULT_TIMEOUT_MS;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_TIMEOUT_MS;
}

function asOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function toErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message)) {
      return 'The request timed out. Please try again.';
    }
    if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
      return 'Cannot reach the toolbox API. Check the backend / network.';
    }
    const data = err.response?.data as unknown;
    if (data && typeof data === 'object') {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0] as { msg?: unknown };
        if (typeof first?.msg === 'string' && first.msg.trim()) return first.msg;
      }
      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (err.response?.status === 401) return 'Authentication required.';
    if (err.response?.status === 403) return 'Access denied.';
    if (err.response?.status === 404) return 'Toolbox resource not found.';
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function asElementType(value: unknown, fallback: ElementType = 'rectangle'): ElementType {
  if (typeof value === 'string' && KNOWN_ELEMENT_TYPES.has(value)) {
    return value as ElementType;
  }
  return fallback;
}

function mapBomUnit(value: unknown): ToolboxBomUnit {
  return typeof value === 'string' && BOM_UNIT_VALUES.has(value)
    ? (value as ToolboxBomUnit)
    : 'pcs';
}

function toApiBomEntry(row: ToolboxBomLine): ApiBomEntry {
  return {
    id: row.id,
    name: row.name,
    qty: row.qty,
    category: row.category?.trim() ? row.category.trim() : null,
    description: row.description?.trim() ? row.description.trim() : null,
    unit: row.unit,
    price: TOOLBOX_BOM_CATALOG_PRICE,
  };
}

function mapBom(entries: ApiBomEntry[] | undefined): ToolboxBomLine[] | undefined {
  if (!entries?.length) return undefined;
  return entries.map((row, index) => ({
    id: asOptionalString(row.id) ?? `bom-${index}`,
    name: String(row.name ?? ''),
    qty: typeof row.qty === 'number' ? row.qty : 1,
    category: asOptionalString(row.category),
    description: asOptionalString(row.description),
    unit: mapBomUnit(row.unit),
  }));
}

function mapCustomFields(
  fields: ApiCustomField[] | undefined
): ToolboxCustomField[] | undefined {
  if (!fields?.length) return undefined;
  return fields.map((field, index) => ({
    id: asOptionalString(field.id) ?? `cf-${index}`,
    label: String(field.label ?? ''),
    value: field.value == null ? '' : String(field.value),
    unit: asOptionalString(field.unit ?? undefined),
  }));
}

export function mapApiProductType(raw: ApiProductType): ToolboxProductType {
  const elementTypes = (raw.elementTypes ?? [])
    .map((t) => asElementType(t, 'rectangle'))
    .filter((t, i, arr) => arr.indexOf(t) === i);
  return {
    id: String(raw.id),
    label: String(raw.label ?? raw.id),
    color: asOptionalString(raw.color) ?? '#64748b',
    elementTypes: elementTypes.length > 0 ? elementTypes : ['rectangle'],
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 0,
    isSystem: Boolean(raw.isSystem),
    toolCount: typeof raw.toolCount === 'number' ? raw.toolCount : 0,
  };
}

export function mapApiTool(
  raw: ApiTool,
  productTypes: ToolboxProductType[]
): ToolboxItem {
  const product = productTypes.find((p) => p.id === raw.productTypeId);
  const dims = raw.defaultDimensions ?? {};
  return {
    id: String(raw.id),
    productTypeId: String(raw.productTypeId),
    type: asElementType(raw.type, product?.elementTypes[0] ?? 'rectangle'),
    name: String(raw.name ?? 'Tool'),
    icon: String(raw.icon || '◆'),
    sku: asOptionalString(raw.sku),
    description: asOptionalString(raw.description),
    category: product?.label,
    defaultDimensions: {
      width: typeof dims.width === 'number' && dims.width > 0 ? dims.width : 40,
      height: typeof dims.height === 'number' && dims.height > 0 ? dims.height : 40,
      depth: typeof dims.depth === 'number' && dims.depth > 0 ? dims.depth : undefined,
    },
    defaultRotation: typeof raw.defaultRotation === 'number' ? raw.defaultRotation : 0,
    defaultProperties: raw.defaultProperties ? { ...raw.defaultProperties } : undefined,
    enabledParams: Array.isArray(raw.enabledParams) ? [...raw.enabledParams] : undefined,
    customFields: mapCustomFields(raw.customFields),
    bom: mapBom(raw.bom),
    weightKg: typeof raw.weightKg === 'number' ? raw.weightKg : undefined,
    visibleInToolbox: raw.visibleInToolbox !== false,
  };
}

function mapCatalogResponse(payload: unknown): ToolboxCatalog {
  const record = (payload && typeof payload === 'object' ? payload : {}) as ApiCatalogResponse;
  const productTypes = (record.productTypes ?? []).map(mapApiProductType);
  const tools = (record.tools ?? []).map((tool) => mapApiTool(tool, productTypes));
  const standardParams = (record.standardParams ?? [])
    .filter((p) => typeof p?.key === 'string' && typeof p?.label === 'string')
    .map((p) => ({
      key: p.key,
      label: p.label,
      unit: typeof p.unit === 'string' ? p.unit : null,
      defaultValue: typeof p.defaultValue === 'number' ? p.defaultValue : null,
    }));
  return { productTypes, tools, standardParams };
}

function toolToApiPayload(tool: ToolboxItem, productTypeId: string): ApiTool {
  return {
    id: tool.id,
    productTypeId,
    type: tool.type,
    name: tool.name,
    icon: tool.icon || '◆',
    sku: tool.sku ?? null,
    description: tool.description ?? null,
    defaultDimensions: {
      width: tool.defaultDimensions.width,
      height: tool.defaultDimensions.height,
      depth: tool.defaultDimensions.depth ?? null,
    },
    defaultRotation: tool.defaultRotation ?? 0,
    defaultProperties: tool.defaultProperties ?? {},
    enabledParams: tool.enabledParams ?? [],
    customFields: (tool.customFields ?? []).map((field) => ({
      id: field.id,
      label: field.label,
      value: field.value,
      unit: field.unit ?? null,
    })),
    bom: (tool.bom ?? []).map(toApiBomEntry),
    weightKg: tool.weightKg ?? null,
    visibleInToolbox: tool.visibleInToolbox !== false,
  };
}

function productTypeToApiPayload(product: ToolboxProductType): ApiProductType {
  return {
    id: product.id,
    label: product.label,
    color: product.color,
    elementTypes: product.elementTypes,
    sortOrder: product.sortOrder ?? 0,
    isSystem: product.isSystem ?? false,
  };
}

/** Resolve which product type a draft tool belongs to. */
export function resolveToolProductTypeId(
  tool: ToolboxItem,
  productTypes: ToolboxProductType[]
): string {
  if (tool.productTypeId && productTypes.some((p) => p.id === tool.productTypeId)) {
    return tool.productTypeId;
  }
  const byType = productTypes.find((p) => p.elementTypes.includes(tool.type));
  if (byType) return byType.id;
  const byLabel = productTypes.find(
    (p) => p.label.toLowerCase() === (tool.category ?? '').toLowerCase()
  );
  if (byLabel) return byLabel.id;
  return productTypes[0]?.id ?? 'annotation';
}

export function newToolboxToolId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`;
}

/** Horizontal and vertical straight rails share direction, length, and BOM. */
export const LINKED_STRAIGHT_TRACK_TOOL_ID = 'rail-at1';
export const LINKED_VERTICAL_TRACK_TOOL_ID = 'rail-at1-vertical';

export function linkedStraightTrackTwinId(toolId: string): string | null {
  if (toolId === LINKED_STRAIGHT_TRACK_TOOL_ID) return LINKED_VERTICAL_TRACK_TOOL_ID;
  if (toolId === LINKED_VERTICAL_TRACK_TOOL_ID) return LINKED_STRAIGHT_TRACK_TOOL_ID;
  return null;
}

function cloneToolboxBom(bom: ToolboxBomLine[] | undefined): ToolboxBomLine[] {
  return (bom ?? []).map((row) => ({ ...row }));
}

function applyLinkedStraightTrackSharedFields(
  target: ToolboxItem,
  source: ToolboxItem
): ToolboxItem {
  return {
    ...target,
    bom: cloneToolboxBom(source.bom),
    defaultProperties: {
      ...(target.defaultProperties ?? {}),
      direction: source.defaultProperties?.direction,
      lengthMeters: source.defaultProperties?.lengthMeters,
    },
  };
}

function createLinkedStraightTrackTwin(source: ToolboxItem, twinId: string): ToolboxItem {
  const isVertical = twinId === LINKED_VERTICAL_TRACK_TOOL_ID;
  return applyLinkedStraightTrackSharedFields(
    {
      ...source,
      id: twinId,
      name: isVertical ? 'Vertical Track' : 'Track',
      sku: isVertical ? 'AeroTrak AT1-V' : 'AeroTrak AT1',
      icon: isVertical ? '│' : '─',
      description: isVertical
        ? 'Vertical straight track segment'
        : 'Straight track segment',
      defaultRotation: isVertical ? 90 : 0,
    },
    source
  );
}

/**
 * Copy direction, lengthMeters, and bom from `sourceId` onto its linked twin
 * so both straight-rail catalog entries stay aligned in the API payload.
 */
export function syncLinkedStraightTrackTools(
  tools: ToolboxItem[],
  sourceId: string
): ToolboxItem[] {
  const twinId = linkedStraightTrackTwinId(sourceId);
  if (!twinId) return tools;
  const source = tools.find((tool) => tool.id === sourceId);
  if (!source) return tools;

  const twin = tools.find((tool) => tool.id === twinId);
  if (!twin) {
    return [...tools, createLinkedStraightTrackTwin(source, twinId)];
  }

  return tools.map((tool) =>
    tool.id === twinId ? applyLinkedStraightTrackSharedFields(tool, source) : tool
  );
}

/** GET /api/v1/toolbox/catalog */
export async function fetchToolboxCatalog(options?: {
  timeoutMs?: number;
}): Promise<ToolboxCatalog> {
  try {
    const response = await http.get(getToolboxCatalogUrl(), {
      timeout: options?.timeoutMs ?? readTimeoutMs(),
    });
    return mapCatalogResponse(response.data);
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to load toolbox catalog.'));
  }
}

/** PUT /api/v1/toolbox/catalog — full draft publish (Save Changes). */
export async function saveToolboxCatalog(
  productTypes: ToolboxProductType[],
  tools: ToolboxItem[],
  options?: { timeoutMs?: number; linkedStraightTrackSourceId?: string }
): Promise<ToolboxCatalog> {
  const syncedTools = options?.linkedStraightTrackSourceId
    ? syncLinkedStraightTrackTools(tools, options.linkedStraightTrackSourceId)
    : tools;
  const body = {
    productTypes: productTypes.map(productTypeToApiPayload),
    tools: syncedTools.map((tool) =>
      toolToApiPayload(tool, resolveToolProductTypeId(tool, productTypes))
    ),
  };
  try {
    const response = await http.put(getToolboxCatalogUrl(), body, {
      timeout: options?.timeoutMs ?? readTimeoutMs(),
    });
    return mapCatalogResponse(response.data);
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to save toolbox catalog.'));
  }
}

/** POST /api/v1/toolbox/catalog/reset */
export async function resetToolboxCatalog(
  scope = 'all',
  options?: { timeoutMs?: number }
): Promise<ToolboxCatalog> {
  try {
    const response = await http.post(
      getToolboxCatalogResetUrl(),
      { scope },
      { timeout: options?.timeoutMs ?? readTimeoutMs() }
    );
    return mapCatalogResponse(response.data);
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to reset toolbox catalog.'));
  }
}

/** POST /api/v1/toolbox/product-types */
export async function createToolboxProductType(
  payload: { label: string; color?: string | null; elementTypes?: string[] },
  options?: { timeoutMs?: number }
): Promise<ToolboxProductType> {
  try {
    const response = await http.post(getToolboxProductTypesUrl(), payload, {
      timeout: options?.timeoutMs ?? readTimeoutMs(),
    });
    return mapApiProductType(response.data as ApiProductType);
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to create product type.'));
  }
}

/** POST /api/v1/toolbox/tools */
export async function createToolboxTool(
  tool: ToolboxItem,
  productTypeId: string,
  options?: { timeoutMs?: number }
): Promise<ToolboxItem> {
  const { id: _omitId, ...createBody } = toolToApiPayload(tool, productTypeId);
  void _omitId;
  try {
    const response = await http.post(getToolboxToolsUrl(), createBody, {
      timeout: options?.timeoutMs ?? readTimeoutMs(),
    });
    return mapApiTool(response.data as ApiTool, [
      {
        id: productTypeId,
        label: tool.category ?? productTypeId,
        color: '#64748b',
        elementTypes: [tool.type],
      },
    ]);
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to create tool.'));
  }
}

/** PATCH /api/v1/toolbox/tools/{tool_id} */
export async function updateToolboxTool(
  toolId: string,
  patch: Partial<ToolboxItem> & { productTypeId?: string },
  options?: { timeoutMs?: number }
): Promise<ToolboxItem> {
  const body: Record<string, unknown> = {};
  if (patch.productTypeId !== undefined) body.productTypeId = patch.productTypeId;
  if (patch.type !== undefined) body.type = patch.type;
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.icon !== undefined) body.icon = patch.icon;
  if (patch.sku !== undefined) body.sku = patch.sku;
  if (patch.description !== undefined) body.description = patch.description;
  if (patch.defaultDimensions !== undefined) {
    body.defaultDimensions = {
      width: patch.defaultDimensions.width,
      height: patch.defaultDimensions.height,
      depth: patch.defaultDimensions.depth ?? null,
    };
  }
  if (patch.defaultRotation !== undefined) body.defaultRotation = patch.defaultRotation;
  if (patch.defaultProperties !== undefined) body.defaultProperties = patch.defaultProperties;
  if (patch.enabledParams !== undefined) body.enabledParams = patch.enabledParams;
  if (patch.customFields !== undefined) {
    body.customFields = patch.customFields.map((field) => ({
      id: field.id,
      label: field.label,
      value: field.value,
      unit: field.unit ?? null,
    }));
  }
  if (patch.bom !== undefined) {
    body.bom = patch.bom.map(toApiBomEntry);
  }
  if (patch.weightKg !== undefined) body.weightKg = patch.weightKg;
  if (patch.visibleInToolbox !== undefined) body.visibleInToolbox = patch.visibleInToolbox;

  try {
    const response = await http.patch(getToolboxToolUrl(toolId), body, {
      timeout: options?.timeoutMs ?? readTimeoutMs(),
    });
    const productTypes: ToolboxProductType[] = patch.productTypeId
      ? [
          {
            id: patch.productTypeId,
            label: patch.category ?? patch.productTypeId,
            color: '#64748b',
            elementTypes: patch.type ? [patch.type] : ['rectangle'],
          },
        ]
      : [];
    return mapApiTool(response.data as ApiTool, productTypes);
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to update tool.'));
  }
}
